/**
 * **Backswing Cut follow-up reservations** — the intent-free half of the room's cash-in match,
 * extracted so the frontend affordance (the re-lit Skirmish row) and the wall
 * (`backend/src/encounterRoom.ts`'s `stampBackswingFollowup`) read ONE derivation and can't
 * drift, mirroring `legalDirectedDeployCells` (`./deploy.ts`) for a shared legality/affordance
 * enumeration.
 *
 * A **live Backswing reservation** is a status instance on `attacker` that is: (1)
 * **self-sourced** (`source === attacker.id` — only the attacker's own `reserve_followup`
 * plant counts, never a GM-applied or third-party status of the same shape), (2)
 * **weapon-pinned** (`weapon` set — only `reserve_followup` stamps this facet; it's the
 * structural discriminator of a reservation, see `StatusInstance.weapon`'s doc), and (3) live
 * on the attacker's **own acting turn** (`turn.phase === "acting" && turn.actor ===
 * attacker.id` — a reservation grants nothing off-turn).
 *
 * Deliberately **intent-free**: it returns every live reservation on `attacker`, not a single
 * matched one. The final per-intent equality (same weapon INSTANCE as the specific attack,
 * target ≠ the reservation's excluded `partner`) stays with each caller — `stampBackswingFollowup`
 * matches against `intent.weapon`/the attack's target; the frontend affordance reads this same
 * list to light the Skirmish row without re-deriving the self-sourced/weapon-pinned/acting-turn
 * checks.
 */
import type { Combatant, EncounterState, StatusInstance, Turn } from "@lancer-vtt/domain";

/** Every live Backswing Cut reservation on `attacker`, given the encounter's current `turn`. */
export function liveBackswingReservations(attacker: Combatant, turn: Turn): StatusInstance[] {
  const onActingTurn = turn.phase === "acting" && turn.actor === attacker.id;
  if (!onActingTurn) return [];
  return attacker.statuses.filter((s) => s.weapon !== undefined && s.source === attacker.id);
}

/**
 * The refusal copy for a Backswing Cut follow-up fired back at the reservation's excluded
 * `partner` — RAW (Executioner I, Core talents) requires "a different character". Such an attack never
 * matches `stampBackswingFollowup`, so it arrives as an ordinary second Skirmish and the
 * once-per-turn wall refuses it; the room swaps that generic "already used this action" text for
 * this one when the refused attack is exactly this mismatch (a live reservation on the same
 * weapon instance whose `partner` IS the target). `undefined` for every other attack.
 */
export function backswingExcludedPartnerRefusal(
  state: EncounterState,
  attack: { attacker: string; weapon?: string; targetId?: string },
): string | undefined {
  const attacker = state.combatants[attack.attacker];
  if (attacker === undefined || attack.targetId === undefined) return undefined;
  const excluded = liveBackswingReservations(attacker, state.activation.turn).some(
    (s) => s.weapon === attack.weapon && s.partner === attack.targetId,
  );
  if (!excluded) return undefined;
  const partnerName = state.combatants[attack.targetId]?.name ?? attack.targetId;
  return `backswing: ${attacker.name}'s Backswing Cut follow-up must target a different character than ${partnerName}, the one its triggering hit struck`;
}
